/**
 * Steam mode for a milk drink on a machine with a Manetta board (#257 phase 3, Manetta's steam-mode.md): what the
 * store keeps about steam Taretto switched on, and what to say when it can't. The store (store.ts) runs it:
 * enter_steam_mode until Status shows steam mode, and leave_steam_mode until it shows brew heat. When is the guided
 * brew's to say (pages/brew-wizard/machineSteam.ts).
 *
 * Only steam Taretto switched on is Taretto's to switch off. Steam switched on with the machine's own button shows on
 * the chip and is left alone, and so is steam mode that was already on when Taretto would have switched to it.
 */
import type { MachineCommandResult, MachineState } from './types';

/** enter_steam_mode asks for this cap, so a brew left open after the milk step goes back to brew heat by itself. */
export const STEAM_MAX_MS = 5 * 60_000;
/**
 * After the device took a steam command, how long Status gets to show the machine there before the command goes
 * again. Status can take a few seconds to show it.
 */
export const STEAM_FOLLOW_MS = 6_000;
/** Sends of one steam command before Taretto says to use the machine's own Steam button. */
export const STEAM_ATTEMPTS = 3;

export const STEAM_DIDNT_START = 'The machine didn’t switch to steam. Use its Steam button.';
export const STEAM_STILL_ON = 'Switch steam off on the machine.';

/** Why Taretto switched to steam: the machine reported the shot over, or the brew step ended without that report. */
export type SteamEnterVia = 'shot_over' | 'brew_step';
/**
 * Why Taretto switches back to brew heat: milk in the drink on the scale, the drink step skipped, the brew saved,
 * the brew left (Exit, or another page), or back to the shot to pull it again.
 */
export type SteamLeaveVia = 'drink' | 'skip' | 'after_save' | 'exit' | 'back';

/** Steam mode Taretto switched on, until it's over. */
export interface SteamSession {
  /** Tells one session from the next, for the loops that run on after it's replaced. */
  id: number;
  /** performance.now() when enter_steam_mode went out. */
  at: number;
  /**
   * entering (enter_steam_mode on its way) → on (the device took it) → leaving (leave_steam_mode until Status shows
   * brew heat) → off (Status shows brew heat after steam mode, however it ended).
   */
  phase: 'entering' | 'on' | 'leaving' | 'off';
  /** Status has shown steam mode since. Until then, brew heat is the board not having read the machine yet. */
  seen: boolean;
  /** ms from `at` to when Status first showed steam ready; null before. */
  readyAfterMs: number | null;
  /** leave_steam_mode went STEAM_ATTEMPTS times without Status showing brew heat: switch it off on the machine. */
  leaveLate: boolean;
}

/** Status shows steam mode: heating for steam or ready. */
export function inSteam(s: Pick<MachineState, 'steam'> | null): boolean {
  return s?.steam === 'heating' || s?.steam === 'ready';
}

/** Taretto's steam is still on, as far as Taretto knows. */
export function steamOn(steam: SteamSession | null): boolean {
  return steam != null && steam.phase !== 'off';
}

/** Why enter_steam_mode didn't switch to steam, in words for someone at the machine. */
export function steamProblem(result: MachineCommandResult): string {
  switch (result) {
    case 'not_ready':
      return 'The machine isn’t ready for steam: it’s off or still heating. Use its Steam button once it’s ready.';
    case 'busy':
      return 'The machine is brewing, so it didn’t switch to steam. Use its Steam button after the shot.';
    case 'fault':
      return 'The machine reports a fault, so it didn’t switch to steam. Check it.';
    case 'unsupported':
      return 'This machine can’t switch to steam.';
    case 'invalid':
      return 'The machine didn’t take the command. Its firmware may not match this version of Taretto.';
    case 'no_reply':
      return 'The machine didn’t answer, so it didn’t switch to steam. Make sure it’s plugged in and nearby.';
    case 'ok':
      return '';
  }
}

/** How long the machine took to heat for steam, for usage data (machine_steam): nobody has timed it on most machines. */
export function steamReadyBucket(readyAfterMs: number | null): 'under_30s' | '30_60s' | '60_120s' | 'over_120s' | 'never' {
  if (readyAfterMs == null) return 'never';
  if (readyAfterMs < 30_000) return 'under_30s';
  if (readyAfterMs < 60_000) return '30_60s';
  if (readyAfterMs < 120_000) return '60_120s';
  return 'over_120s';
}
